import { useEffect, useState, useCallback, useRef } from 'react'
import { getLocalStorageNumber, setLocalStorageValue } from 'shared/utils'

export enum PaginationSource {
  ProfileAccessCodeList = 'ProfileAccessCodeList',
  History = 'History',
  UsersList = 'UsersList',
  ZonesList = 'ZonesList',
  AccessProfilesList = 'AccessProfilesList',
  AccessCodeUsersList = 'AccessCodeUsersList',
  ProfileUsersList = 'ProfileUsersList',
  ResourceProfileUsersList = 'ResourceProfileUsersList',
  ResourceUsersList = 'ResourceUsersList',
  UserProfilesList = 'UserProfilesList',
  UserResourceProfilesList = 'UserResourceProfilesList',
  ZoneAdmins = 'ZoneAdmins',
  EditLocksForm = 'EditLocksForm',
  ZoneUsersList = 'ZoneUsersList',
  BuildingList = 'UseBuildingList',
  EditUsersListDialog = 'EditUsersListDialog',
  EditProfilesListDialog = 'EditProfilesListDialog',
  AddZoneAdminDialog = 'AddZoneAdminDialog',
  Organizations = 'Organizations',
  Profiles = 'Profiles',
  ResourceProfiles = 'ResourceProfiles',
  Resources = 'Resources',
  ResourcesBookings = 'ResourcesBookings',
  Users = 'Users',
  Zones = 'Zones',
  Locks = 'Locks',
  OrganizationAdmins = 'OrganizationAdmins',
  ProfileUserMembershipsList = 'ProfileUserMembershipsList',
  VisitorCheckInRequest = 'VisitorCheckInRequest',
  VisitorCheckInTargets = 'VisitorCheckInTargets',
  NetworkMonitoring = 'NetworkMonitoring',
  LockDevices = 'LockDevices',
  LockDeviceFirmwarePicker = 'LockDeviceFirmwarePicker',
  LockDeviceFirmwareFilter = 'LockDeviceFirmwareFilter',
  AddOrganizationAdminDialog = 'AddOrganizationAdminDialog',
  LocksPage = 'LocksPage',
  ZoneLocksList = 'ZoneLocksList',
  AccessProfileZonesList = 'AccessProfileZonesList',
  AccessProfileLocksList = 'AccessProfileLocksList',
  ResourceProfileZonesList = 'ResourceProfileZonesList',
  ResourceProfileResourcesList = 'ResourceProfileResourcesList',
  ResourceZonesList = 'ResourceZonesList',
  ResourceLocationsList = 'ResourceLocationsList',
}

const cacheKey = (source: PaginationSource) => `Pagination_OnPage_${source}`

export const getPageOnBySource = (source: PaginationSource): number | null =>
  getLocalStorageNumber(cacheKey(source))

export interface PaginationProps {
  page: number
  pageCount: number
  onPage: number
  changePageSize: (onPage: number) => void
  goNextPage: () => void
  goPrevPage: () => void
  goFirstPage: () => void
  startCursor: string | undefined | null
  endCursor: string | undefined | null
  canGoNextPage: boolean
  canGoPrevPage: boolean
  initPageSize: number
  pageSizeOptions: number[]
}

export interface SearchProps {
  query: string
  setQuery: (query: string) => any
}

interface Variables {
  query?: string
  first?: number
  last?: number
  after?: string
  before?: string
}

interface Props {
  initPageSize?: number
  pageSizeOptions?: number[]
  pageInfo:
    | {
        endCursor?: string | null
        hasNextPage?: boolean | null
        startCursor?: string | null
        hasPreviousPage?: boolean | null
      }
    | undefined
  loading: boolean
  totalCount: number
  refetch: (variables: Variables) => void | Promise<unknown>
  source: PaginationSource
  initialQuery?: string
  initialPage?: number
  debounce?: number
}

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

export const withNameFilter = ({ query, ...variables }: Variables) => ({
  ...variables,
  name: query || undefined,
})

export const isValidPageSize = (
  pageSize: number | null | undefined,
  pageSizeOptions: readonly number[]
): pageSize is number =>
  pageSize !== null &&
  pageSize !== undefined &&
  pageSizeOptions.includes(pageSize)

export const getValidPageSize = (
  pageSize: number | null | undefined,
  pageSizeOptions: readonly number[],
  fallback?: number
) => {
  const validFallback = isValidPageSize(fallback, pageSizeOptions)
    ? fallback
    : pageSizeOptions[0] ?? PAGE_SIZE_OPTIONS[0]

  return isValidPageSize(pageSize, pageSizeOptions) ? pageSize : validFallback
}

export const getInitialPageSize = ({
  pageSizeFromUrl,
  cachedPageSize,
  pageSizeOptions,
  fallback,
}: {
  pageSizeFromUrl?: number | null
  cachedPageSize?: number | null
  pageSizeOptions: readonly number[]
  fallback?: number
}) =>
  isValidPageSize(pageSizeFromUrl, pageSizeOptions)
    ? pageSizeFromUrl
    : getValidPageSize(cachedPageSize, pageSizeOptions, fallback)

export const usePagination = ({
  initPageSize,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  pageInfo,
  loading,
  totalCount,
  refetch,
  source,
  initialQuery = '',
  initialPage = 1,
  debounce = 400,
}: Props) => {
  const [query, setQuery] = useState(initialQuery)
  const [requestedQuery, setRequestedQuery] = useState(initialQuery)

  const cachedOnPage = getLocalStorageNumber(cacheKey(source))

  const initialOnPage = isValidPageSize(initPageSize, pageSizeOptions)
    ? initPageSize
    : getValidPageSize(cachedOnPage, pageSizeOptions)

  const cachedOnPageIsValid = isValidPageSize(cachedOnPage, pageSizeOptions)

  const [onPage, setOnPage] = useState(initialOnPage)
  const [page, setPage] = useState(initialPage)

  const [totalCountState, setTotalCountState] = useState(totalCount)

  useEffect(() => {
    if (
      cachedOnPage !== null &&
      cachedOnPage !== undefined &&
      !cachedOnPageIsValid
    ) {
      setLocalStorageValue(cacheKey(source), initialOnPage)
    }
  }, [cachedOnPage, cachedOnPageIsValid, initialOnPage, source])

  useEffect(() => {
    if (!loading) {
      setTotalCountState(totalCount)
    }
  }, [loading, totalCount])

  const searchRequestIdRef = useRef(0)

  const safeRefetch = useCallback(
    async (variables: Variables) => {
      try {
        await refetch(variables)
      } catch {
        // request errors are surfaced by the consumer's query state
      }
    },
    [refetch]
  )

  const handleQueryChange = useCallback(
    async (query: string) => {
      const requestId = ++searchRequestIdRef.current

      setPage(1)

      await safeRefetch({
        query,
        first: onPage,
        after: undefined,
        last: undefined,
        before: undefined,
      })

      if (searchRequestIdRef.current === requestId) {
        setRequestedQuery(query)
      }
    },
    [onPage, safeRefetch]
  )

  const didMount = useRef(false)

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true
      return
    }

    if (query === requestedQuery) return

    const timeoutId = setTimeout(() => {
      void handleQueryChange(query)
    }, debounce)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, requestedQuery, debounce])

  const pageCount = Math.ceil(totalCountState / onPage)

  const startCursor = pageInfo?.startCursor

  const endCursor = pageInfo?.endCursor

  const goFirstPage = useCallback(() => {
    setPage(1)
    void safeRefetch({
      query: query ? query : undefined,
      first: onPage,
      after: undefined,
      last: undefined,
      before: undefined,
    })
  }, [onPage, safeRefetch, query])

  const goNextPage = useCallback(() => {
    if (endCursor) {
      setPage(page => page + 1)
      void safeRefetch({
        query: query ? query : undefined,
        first: onPage,
        after: endCursor,
        last: undefined,
        before: undefined,
      })
    } else {
      goFirstPage()
    }
  }, [onPage, endCursor, safeRefetch, query, goFirstPage])

  const goPrevPage = useCallback(() => {
    if (startCursor) {
      setPage(page => page - 1)
      void safeRefetch({
        query: query ? query : undefined,
        first: undefined,
        after: undefined,
        last: onPage,
        before: startCursor,
      })
    } else {
      goFirstPage()
    }
  }, [onPage, startCursor, safeRefetch, query, goFirstPage])

  const changePageSize = useCallback(
    (newOnPage: number) => {
      if (
        !isValidPageSize(newOnPage, pageSizeOptions) ||
        newOnPage === onPage
      ) {
        return
      }

      setLocalStorageValue(cacheKey(source), newOnPage)
      setOnPage(newOnPage)
      setPage(1)

      void safeRefetch({
        query: query ? query : undefined,
        first: newOnPage,
        after: undefined,
        last: undefined,
        before: undefined,
      })
    },
    [onPage, pageSizeOptions, query, safeRefetch, source]
  )

  const canGoNextPage = pageCount > page && !loading

  const canGoPrevPage = page > 1 && !loading

  const isSearchPending = query !== requestedQuery

  const resetPage = useCallback(() => setPage(1), [])

  const pagination: PaginationProps = {
    page,
    pageCount,
    onPage,
    changePageSize,
    goNextPage,
    goPrevPage,
    goFirstPage,
    startCursor,
    endCursor,
    canGoNextPage,
    canGoPrevPage,
    initPageSize: initialOnPage,
    pageSizeOptions,
  }

  const search: SearchProps = {
    query,
    setQuery,
  }

  return {
    pagination,
    resetPage,
    search,
    isSearchPending,
  }
}
